/* vsl-track.js: per-second VSL retention tracker (run 08 gout VSL). No dependencies. Contract: analytics/README.md.

   Load BEFORE player.js, not defer/async:
     <script src="vsl-track.js" data-endpoint="https://vsl-analytics.<acct>.workers.dev"></script>
   Endpoint: data-endpoint → window.VSL_TRACK_ENDPOINT → localStorage "vsl_track_endpoint" (QA override).
   Blank everywhere = disabled: window.VSLTrack is a stub whose methods do nothing.

   API (callers guard with `window.VSLTrack && VSLTrack.x()`):
     attach(video)            once; the tracker listens to the element itself
     unmute()                 the first tap that starts playback with sound (muted-preview mode: the "Click Here" tap)
     rewind10()               the -10 s button / key
     reveal(how)              first offer reveal of this page load: "time" | "reload" | "return" | "saved"
     card(bottles)            package-card click (beacons at once: the page navigates next)
     checkout(bottles)        order page → /checkout (only joins an existing session)
     quizRedirect()           the end-card auto-click (beacons at once)
     exitPopup(kind)          "shown" | "clicked"
     event(type, data)        anything else, type = [a-z0-9_]{1,24}

   What it records (one session = one visitor + video, 12 h idle TTL, new ad click = new session):
     snd / mut  bitsets of video seconds actually played (not paused, not seeking, continuous forward progress),
                split by sound on vs muted autoplay; partial sends are OR-merged server-side.
     ev         [index, ms since page load, type, video time, data]; (sid, page-load id, index) is unique, so resends
                are idempotent.
    fbp / fbc  the Meta pixel's _fbp / _fbc cookies (fbc falls back to the URL's fbclid), on every send; the Worker uses
               them only for its Conversions API events and stores neither.
   Internal sessions (?qa=1 sticky until ?qa=0, localStorage vsl_qa=1, navigator.webdriver, headless UA, ?review=1,
   ?preview=1, Shopify theme preview, any host but radiancywellness.com) are sent with qa=1; reports exclude them.
   Crawlers are dropped. */
(function () {
  "use strict";
  var W = window;
  var D = document;
  var N = navigator;
  var ME = D.currentScript;
  var METHODS = ["attach", "unmute", "rewind10", "reveal", "card", "checkout", "quizRedirect", "exitPopup", "event", "flush"];
  var TTL_MS = 12 * 3600 * 1000;
  var CAP_SEC = 4096;           // bitset capacity (seconds); the VSL is 2058 s
  var EVENT_CAP = 400;          // per page load
  var PERIOD_MS = 30000;
  var AD_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id", "fbclid",
    "ad_id", "adset_id", "campaign_id", "placement", "site_source_name"];

  function noop() {}
  function stub() {
    var o = { enabled: false };
    for (var i = 0; i < METHODS.length; i++) o[METHODS[i]] = noop;
    return o;
  }
  function ls(k, v) {
    try {
      if (v === undefined) return localStorage.getItem(k);
      if (v === null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch (e) {}
    return null;
  }

  var P = new URLSearchParams(location.search);
  var UA = N.userAgent || "";
  if (P.get("qa") === "1") ls("vsl_qa", "1");
  else if (P.get("qa") === "0") ls("vsl_qa", null);

  var endpoint = String((ME && ME.getAttribute("data-endpoint")) || W.VSL_TRACK_ENDPOINT || ls("vsl_track_endpoint") || "")
    .trim().replace(/\/+$/, "").replace(/\/t$/, "");
  if (!endpoint || !W.JSON || !W.Uint8Array || /bot\b|crawler|spider|facebookexternalhit|Facebot|Lighthouse/i.test(UA)) {
    W.VSLTrack = stub();
    return;
  }
  var URL_T = endpoint + "/t";
  // Video id (one retention clock per id): the script's data-video, else the R2 key prefix of the store player's hosted
  // source (#player[data-video] = …/v/<id>/vsl08-720.mp4 or …/v/<id>/hls/master.m3u8), else "blake-v4" (local player).
  var hosted = /\/v\/([a-z0-9-]{1,32})\//.exec((D.getElementById("player") || { getAttribute: noop }).getAttribute("data-video") || "");
  // A page without the player (the order page, the quiz) only joins the visitor's existing session: it takes the stored
  // session's video id and never starts a session of its own (2026-10-05: it used to default to "blake-v4", so it never
  // matched the "blake-v6" session and its checkout beacon was dropped).
  var joinOnly = !D.getElementById("player");
  var stored = null;
  try { stored = JSON.parse(ls("vsl_sid") || "null"); } catch (e) {}
  var VIDEO = (ME && ME.getAttribute("data-video")) || (hosted && hosted[1]) || (joinOnly && stored && stored.v) || "blake-v4";
  var QA = ls("vsl_qa") === "1" || N.webdriver === true || /HeadlessChrome|Puppeteer|Playwright/i.test(UA) ||
    P.get("review") === "1" || P.get("preview") === "1" || P.has("preview_theme_id") ||
    !!(W.Shopify && (W.Shopify.designMode || W.Shopify.theme && W.Shopify.theme.role && W.Shopify.theme.role !== "main")) ||
    !/(^|\.)radiancywellness\.com$/i.test(location.hostname) ? 1 : 0;

  function rid(n) {
    var a = new Uint8Array(n);
    (W.crypto || W.msCrypto).getRandomValues(a);
    var s = "";
    for (var i = 0; i < n; i++) s += (a[i] & 31).toString(32);
    return s;
  }

  // ---------- Session ----------
  var T0 = Date.now();
  var meta = {};
  var attrib = "";
  for (var k = 0; k < AD_KEYS.length; k++) {
    var val = P.get(AD_KEYS[k]);
    if (val) {
      meta[AD_KEYS[k]] = val.slice(0, 200);
      if (AD_KEYS[k] === "fbclid" || AD_KEYS[k] === "utm_content" || AD_KEYS[k] === "ad_id" || AD_KEYS[k] === "utm_campaign") attrib += val.slice(0, 40) + "|";
    }
  }
  var fresh = !stored || !stored.id || T0 - stored.ts > TTL_MS ||
    (!joinOnly && (stored.v !== VIDEO || (attrib && stored.a !== attrib)));
  var sess = fresh ? { id: rid(20), v: VIDEO, a: attrib, ts: T0 } : stored;
  var hadSession = !fresh;
  function touch() {
    sess.ts = Date.now();
    ls("vsl_sid", JSON.stringify(sess));
  }

  meta.dev = /iPad|Tablet|PlayBook|Silk|Android(?!.*Mobi)/i.test(UA) ? "t" : /Mobi|iPhone|iPod|Android/i.test(UA) ? "m" : "d";
  meta.os = /iPhone|iPad|iPod/i.test(UA) ? "ios" : /Android/i.test(UA) ? "android" : /Mac OS X/i.test(UA) ? "mac" :
    /Windows/i.test(UA) ? "win" : /Linux|CrOS/i.test(UA) ? "linux" : "other";
  meta.app = /FBAN|FBAV|FB_IAB|FBIOS/i.test(UA) ? "fb" : /Instagram/i.test(UA) ? "ig" : "";
  try { meta.ref = D.referrer ? new URL(D.referrer).hostname : ""; } catch (e) { meta.ref = ""; }
  meta.path = location.pathname.slice(0, 120);

  // ---------- State ----------
  var PL = rid(8);              // this page load
  var snd = new Uint8Array(CAP_SEC >> 3);
  var mut = new Uint8Array(CAP_SEC >> 3);
  var events = [];
  var sent = 0;                 // events[0..sent) delivered (or handed to sendBeacon)
  var dirty = false;
  var attached = false;
  var metaSent = false;
  var video = null;
  var lastT = -1;
  var lastWall = 0;
  var pos = 0;                  // last observed currentTime (seek "from")
  var seekFrom = -1;
  var soundOn = false;
  var revealed = false;
  var inflight = false;

  function vt() { return video ? Math.round((video.currentTime || 0) * 10) / 10 : null; }

  function push(type, data, beaconNow) {
    if (events.length >= EVENT_CAP) return;
    events.push([events.length, Date.now() - T0, type, vt(), data === undefined ? null : data]);
    dirty = true;
    if (beaconNow) flush(true);
  }

  function mark(a, b, muted) {
    var arr = muted ? mut : snd;
    if (b >= CAP_SEC) b = CAP_SEC - 1;
    for (var s = a; s <= b; s++) {
      var bit = 1 << (s & 7);
      if (!(arr[s >> 3] & bit)) {
        arr[s >> 3] |= bit;
        dirty = true;
      }
    }
  }

  // True playback only: playing, not seeking, and the clock moved forward by about the wall time elapsed.
  function tick() {
    if (!video) return;
    var t = video.currentTime || 0;
    if (video.paused || video.seeking) {
      lastT = -1;
      pos = t;
      return;
    }
    var w = Date.now();
    if (lastT >= 0) {
      var dt = t - lastT;
      var dw = (w - lastWall) / 1000;
      if (dt >= 0 && dt <= dw * 1.25 + 0.5) mark(Math.floor(lastT), Math.floor(t), video.muted || video.volume === 0);
    }
    if (!video.muted && video.volume > 0 && !soundOn) markSound("auto");
    lastT = t;
    lastWall = w;
    pos = t;
  }

  function markSound(how) {
    if (soundOn) return;
    soundOn = true;
    push("unmute", how);
  }

  function b64(arr) {
    var n = arr.length;
    while (n > 0 && arr[n - 1] === 0) n--;
    if (!n) return "";
    var s = "";
    for (var i = 0; i < n; i++) s += String.fromCharCode(arr[i]);
    return btoa(s);
  }

  // Meta browser ids for the Worker's Conversions API events (VSLOfferReached / VSLWatch5Min, README "Meta events"):
  // the _fbp / _fbc cookies the store's Meta pixel sets on .radiancywellness.com, read at send time (the pixel sets them
  // after load). No _fbc but an fbclid in the URL: build it the way the pixel does, fb.1.<ms>.<fbclid>. The Worker
  // forwards them to Meta and stores neither.
  function fbIds(o) {
    var c = D.cookie || "";
    var p = /(?:^|;\s*)_fbp=([^;]{1,120})/.exec(c);
    var f = /(?:^|;\s*)_fbc=([^;]{1,600})/.exec(c);
    if (p) o.fbp = p[1];
    if (f) o.fbc = f[1];
    else if (P.get("fbclid")) o.fbc = "fb.1." + T0 + "." + P.get("fbclid").slice(0, 500);
  }

  function body(evs) {
    var o = { v: 1, sid: sess.id, pl: PL, qa: QA, vid: VIDEO, at: T0, s: b64(snd), m: b64(mut), ev: evs };
    if (video && video.duration > 0 && isFinite(video.duration)) o.dur = Math.round(video.duration);
    if (!metaSent) o.meta = meta;
    try { fbIds(o); } catch (e) {}
    return JSON.stringify(o);
  }

  // beacon=true: page is going away (sendBeacon, keepalive fetch fallback). Beacons are best effort (a backgrounded
  // in-app browser can drop them), so they do NOT advance the cursor: the same events go out again with the next
  // confirmed fetch. The server ignores duplicate (session, page load, index) keys. Otherwise a keepalive fetch whose
  // success advances the cursor; failures resend next time.
  function flush(beacon) {
    if (!dirty && sent >= events.length) return;
    if (!attached && !hadSession) return;   // a page without the player only reports into an existing session
    if (inflight && !beacon) return;
    var evs = events.slice(sent, sent + 200);
    var data = body(evs);
    var upto = sent + evs.length;
    touch();
    if (beacon) {
      var ok = false;
      try { ok = N.sendBeacon && N.sendBeacon(URL_T, data); } catch (e) {}
      if (!ok) post(data, true);
      // metaSent stays false until a fetch is confirmed, so a lost first beacon doesn't lose the attribution.
      dirty = true;
      return;
    }
    dirty = false;
    inflight = true;
    post(data, false).then(function (ok) {
      inflight = false;
      if (ok) {
        sent = Math.max(sent, upto);
        metaSent = true;
      } else {
        dirty = true;
      }
      if (sent < events.length) dirty = true;
    });
  }

  function post(data, keepalive) {
    try {
      return fetch(URL_T, {
        method: "POST", body: data, keepalive: true, mode: "cors", credentials: "omit",
        headers: { "Content-Type": "text/plain;charset=UTF-8" }
      }).then(function (r) { return r.ok; }, function () { return false; });
    } catch (e) {
      return { then: function (f) { f(false); } };
    }
  }

  // ---------- Page lifecycle ----------
  D.addEventListener("visibilitychange", function () {
    if (D.visibilityState === "hidden") {
      tick();
      if (attached) push("hide");
      flush(true);
    } else if (attached) {
      push("show");
    }
  });
  W.addEventListener("pagehide", function () {
    tick();
    if (attached) push("exit");
    flush(true);
  });
  setInterval(function () {
    tick();
    flush(false);
  }, PERIOD_MS);

  // ---------- API ----------
  function attach(v) {
    if (attached || !v || !v.addEventListener) return;
    attached = true;
    video = v;
    var nav = null;
    try { nav = performance.getEntriesByType("navigation")[0]; } catch (e) {}
    push("load", (nav && nav.type) || (hadSession ? "return" : "navigate"));
    v.addEventListener("timeupdate", tick);
    v.addEventListener("playing", function () { lastT = -1; tick(); });
    v.addEventListener("play", function () { if (soundOn) push("play"); });
    v.addEventListener("pause", function () {
      tick();
      if (soundOn && !v.ended) push("pause");
    });
    v.addEventListener("seeking", function () {
      if (seekFrom < 0) seekFrom = pos;
      lastT = -1;
    });
    v.addEventListener("seeked", function () {
      var to = v.currentTime || 0;
      if (seekFrom >= 0 && Math.abs(to - seekFrom) >= 1) push("seek", [Math.round(seekFrom * 10) / 10, Math.round(to * 10) / 10]);
      seekFrom = -1;
      pos = to;
      lastT = -1;
      tick();
    });
    v.addEventListener("volumechange", function () {
      if (!v.muted && v.volume > 0 && !v.paused) markSound("auto");
    });
    v.addEventListener("ended", function () {
      tick();
      push("end", null, true);
    });
    setTimeout(function () { flush(false); }, 1500);
  }

  W.VSLTrack = {
    enabled: true,
    qa: !!QA,
    sid: sess.id,
    attach: attach,
    unmute: function () { markSound("click"); },
    rewind10: function () { push("rw10"); },
    reveal: function (how) {
      if (revealed) return;
      revealed = true;
      push("reveal", String(how || "time").slice(0, 12));
    },
    card: function (bottles) { push("card", Number(bottles) || 0, true); },
    checkout: function (bottles) { push("checkout", Number(bottles) || 0, true); },
    quizRedirect: function () { push("quiz", null, true); },
    exitPopup: function (kind) { push(kind === "clicked" ? "exit_click" : "exit_shown"); },
    event: function (type, data) {
      if (!/^[a-z0-9_]{1,24}$/.test(type)) return;
      push(type, data === undefined ? null : typeof data === "number" ? data : String(data).slice(0, 80));
    },
    flush: function () { tick(); flush(false); },
    _state: function () {
      return { sid: sess.id, pl: PL, qa: QA, endpoint: URL_T, events: events.length, sent: sent, snd: b64(snd), mut: b64(mut) };
    }
  };
})();
